import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";

function AssignClassToSectionHead() {
  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [triggerRerender, setTriggerRerender] = useState(false);

  const [clearRows, setClearRows] = useState(false);

  const {
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      sectionHeadId: "",
    },
  });

  const [classesData, setClassesData] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [centersData, setCentersData] = useState([]);
  const [sectionHeadsData, setSectionHeadsData] = useState([]);
  const [classSelectError, setClassSelectError] = useState("");

  useEffect(() => {
    const getUsersByCenterIdAndRoleId = async () => {
      try {
        const response = await get(
          `UserAuthentication/GetUsersByCenterIdAndRoleId?centerId=${centerId}&roleId=${30}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSectionHeadsData(response);
      } catch (error) {
        console.error("Error fetching section heads:", error);
      }
    };
    getUsersByCenterIdAndRoleId();
  }, [centerId]);

  const getAllClassesList = async (sectionHeadId) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `Class/AssignedClassesToSectionHead?sectionHeadId=${sectionHeadId}&centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setClassesData(response);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      omit: true,
    },
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      // width: "60px",
    },
    {
      name: "Year Group",
      selector: (row) => row.name,
      sortable: true,
    },
  ];

  const handleRowSelected = (state) => {
    const selectedRows = state.selectedRows.map((row) => ({
      id: row.id,
      status: true,
    }));

    const unselectedRows = classesData
      .filter(
        (row) => !state.selectedRows.some((selected) => selected.id === row.id)
      )
      .map((row) => ({
        id: row.id,
        status: false,
      }));
    // Combine selected and unselected rows
    setSelectedClassIds([...selectedRows, ...unselectedRows]);
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    if (selectedClassIds.length === 0) {
      setClassSelectError("Please select at least one class.");
      return;
    } else {
      setClassSelectError("");
    }

    const formData = {
      createdBy: localStorage.getItem("userId"),
      classIds: selectedClassIds,
      sectionHeadId: data.sectionHeadId.value,
      centerId: centerId,
    };

    // console.log(formData);
    // return;

    try {
      const response = await post("Class/SaveSectionHeadClasses", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        toast.success("Success!");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">
          Assign Year Groups to Section Head
        </h2>
      </div>

      <form
        className="row g-3 new-form-style"
        onSubmit={handleSubmit(saveData)}
      >
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Section Head<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="sectionHeadId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={sectionHeadsData.map((item) => ({
                          value: item.id,
                          label: `${item.firstName} ${item.lastName}`,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue(`sectionHeadId`, null);
                          } else {
                            setValue(
                              `sectionHeadId`,
                              selectedOption || { value: 0, label: "" }
                            );
                            getAllClassesList(selectedOption.value);
                          }
                          trigger(`sectionHeadId`);
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.sectionHeadId && (
                    <small className="text-danger">
                      {errors.sectionHeadId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-12">
                  <label className="form-label">
                    Classes<span className="text-danger fs-9">*</span>
                  </label>

                  <small className="text-danger">{classSelectError}</small>

                  <DataTable
                    columns={columns}
                    data={classesData}
                    pagination
                    dense
                    customStyles={customTableStyles}
                    highlightOnHover={true}
                    // pointerOnHover={true}
                    selectableRows
                    onSelectedRowsChange={handleRowSelected}
                    onChangePage={setCurrentPage}
                    onChangeRowsPerPage={setRowsPerPage}
                    clearSelectedRows={clearRows}
                    selectableRowSelected={useMemo(
                      () => (row) => row.status === true, // Automatically check rows with status 1
                      [classesData]
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default AssignClassToSectionHead;
